import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { StudentRegistrationFormData, SchoolClass } from '../../types';
import { User, Calendar, Users, GraduationCap } from 'lucide-react';
import { schoolDataApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';

interface StudentRegistrationFormProps {
  onSubmit: (data: StudentRegistrationFormData) => void;
  loading?: boolean;
  initialData?: Partial<StudentRegistrationFormData>;
  onCancel?: () => void;
}

const StudentRegistrationForm: React.FC<StudentRegistrationFormProps> = ({
  onSubmit,
  loading = false,
  initialData,
  onCancel
}) => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: initialData
  });

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setClassesLoading(true);
      const schoolClasses = await schoolDataApi.classes.getAll();
      const sortedClasses = sortClasses(schoolClasses);
      setClasses(sortedClasses);
    } catch (error) {
      console.error('Error loading classes:', error);
      // Fallback to default classes if API fails
      const fallbackClasses = [
        'Nursery', 'LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'
      ].map((name, index) => ({
        id: `fallback-${index}`,
        name,
        sections: ['A'],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }));
      const sortedFallbackClasses = sortClasses(fallbackClasses);
      setClasses(sortedFallbackClasses);
    } finally {
      setClassesLoading(false);
    }
  };

  const onFormSubmit = (data: any) => {
    onSubmit(data as StudentRegistrationFormData);
  };

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#f3ede2] mb-2">
          Student Registration
        </h2>
        <p className="text-[#b9ad9b]">
          Fill in basic information to register for admission
        </p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-[#e2916d]" />
            Student Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Student Name *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Student name is required' })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter student name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                {...register('dateOfBirth', { required: 'Date of birth is required' })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              />
              {errors.dateOfBirth && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.dateOfBirth.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Gender *
              </label>
              <select
                {...register('gender', { required: 'Gender is required' })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.gender.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Preferred Class *
              </label>
              <select
                {...register('preferredClass', { required: 'Preferred class is required' })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                disabled={classesLoading}
              >
                <option value="">
                  {classesLoading ? 'Loading classes...' : 'Select Preferred Class'}
                </option>
                {sortClasses(classes.filter(cls => cls.isActive))
                  .map((cls) => (
                    <option key={cls.id} value={cls.name}>{cls.name}</option>
                  ))}
              </select>
              {errors.preferredClass && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.preferredClass.message}</p>
              )}
              {!classesLoading && classes.length === 0 && (
                <p className="mt-1 text-sm text-[#f0b64c]">
                  No classes available. Please contact admin.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Parent Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
            Parent Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Name *
              </label>
              <input
                type="text"
                {...register('parentName', { required: 'Parent name is required' })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter parent name"
              />
              {errors.parentName && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Contact *
              </label>
              <input
                type="tel"
                {...register('parentContact', {
                  required: 'Parent contact is required',
                  pattern: {
                    value: /^[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit mobile number'
                  }
                })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="10-digit mobile number"
              />
              {errors.parentContact && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentContact.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Email
              </label>
              <input
                type="email"
                {...register('parentEmail', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address'
                  }
                })}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter parent email address"
              />
              {errors.parentEmail && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentEmail.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-[#3a3122]">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            <span>Submit Registration</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentRegistrationForm;